/** Map a persisted review record back into wizard state. */

import { applyCheatSheetVariantAutoNaForRun } from "./cheatSheetVariantChecks";
import type { AgentRun, ArtefactRatings, PhaseRatings, ReviewRecord, ReviewPhase } from "./types";

function phaseRatingFromRecord(record: ReviewRecord, phase: ReviewPhase) {
  const entry = record.ratings.find((r) => r.step_type === phase);
  const answers = Object.fromEntries((entry?.checks ?? []).map((c) => [c.id, c.answer]));
  const check_comments = Object.fromEntries(
    (entry?.checks ?? [])
      .filter((c) => c.comment?.trim())
      .map((c) => [c.id, c.comment!.trim()]),
  );
  return {
    answers,
    check_comments,
    suggested_rag: entry?.suggested_rag ?? null,
    human_rag: entry?.human_rag ?? null,
    human_note: entry?.human_note ?? "",
  };
}

export function phaseRatingsFromReview(record: ReviewRecord): PhaseRatings {
  return {
    orchestration: phaseRatingFromRecord(record, "orchestration"),
    output: phaseRatingFromRecord(record, "output"),
  };
}

export function artefactRatingsFromReview(
  record: ReviewRecord,
  artifactIds: string[] = [],
  run?: AgentRun | null,
): ArtefactRatings {
  const out: ArtefactRatings = {};
  for (const id of artifactIds) {
    out[id] = {
      answers: {},
      check_comments: {},
      suggested_rag: null,
      human_rag: null,
      human_note: "",
      human_approval: null,
    };
  }
  for (const entry of record.artefact_ratings ?? []) {
    out[entry.artifact_id] = {
      answers: Object.fromEntries((entry.checks ?? []).map((c) => [c.id, c.answer])),
      check_comments: Object.fromEntries(
        (entry.checks ?? [])
          .filter((c) => c.comment?.trim())
          .map((c) => [c.id, c.comment!.trim()]),
      ),
      suggested_rag: entry.suggested_rag ?? null,
      human_rag: entry.human_rag ?? null,
      human_note: entry.human_note ?? "",
      human_approval: entry.human_approval ?? null,
    };
  }
  return run ? applyCheatSheetVariantAutoNaForRun(run, out) : out;
}

export function initialArtefactRatings(run: AgentRun): ArtefactRatings {
  const artefacts = run.artefacts ?? [];
  const out: ArtefactRatings = {};
  for (const artefact of artefacts) {
    out[artefact.artifact_id] = {
      answers: {},
      check_comments: {},
      suggested_rag: null,
      human_rag: null,
      human_note: "",
      human_approval: null,
    };
  }
  return applyCheatSheetVariantAutoNaForRun(run, out);
}

export function emptyArtefactRatings(artifactIds: string[]): ArtefactRatings {
  const out: ArtefactRatings = {};
  for (const id of artifactIds) {
    out[id] = {
      answers: {},
      check_comments: {},
      suggested_rag: null,
      human_rag: null,
      human_note: "",
      human_approval: null,
    };
  }
  return out;
}
